import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  X, 
  Download,
} from 'lucide-react';

// Inject custom scrollbar styles
if (typeof document !== 'undefined') {
  const styleId = 'custom-scrollbar-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-track {
        background: var(--color-bg-primary);
        border-radius: 4px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: var(--color-border);
        border-radius: 4px;
        transition: background 0.2s;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: var(--color-text-muted);
      }
      
      /* Firefox scrollbar */
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--color-border) var(--color-bg-primary);
      }
    `;
    document.head.appendChild(style);
  }
}

interface CSVUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  importType: 'insurances' | 'locations' | 'conditions' | 'customers' | 'appointment-details' | 'providers';
  onFileSelected?: (csvData: string) => void;
}

type UploadStage = 'upload' | 'preview' | 'processing' | 'completed' | 'failed';

export const CSVUploadModal: React.FC<CSVUploadModalProps> = ({
  isOpen,
  onClose,
  importType,
  onFileSelected
}) => {
  const [stage, setStage] = useState<UploadStage>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import mutations - currently unused but kept for future CSV import functionality
  // const [csvData, setCsvData] = useState<string>('');
  // const [importInsurances] = useMutation(IMPORT_INSURANCES);
  // const [importLocations] = useMutation(IMPORT_LOCATIONS);
  // const [importConditions] = useMutation(IMPORT_CONDITIONS);
  // const [importCustomers] = useMutation(IMPORT_CUSTOMERS);
  // const [importAppointmentDetails] = useMutation(IMPORT_APPOINTMENT_DETAILS);
  // const [importProviders] = useMutation(IMPORT_PROVIDERS);

  const getImportTypeInfo = () => {
    switch (importType) {
      case 'insurances':
        return {
          title: 'Import Insurances',
          description: 'Upload a CSV file with insurance data. Hover over any column name below for details.',
          requiredColumns: [],
          columnGrid: [
            { name: 'Name', required: true, description: 'Insurance company name', example: 'Blue Cross Blue Shield' },
            { name: 'Description', required: false, description: 'Additional details about the insurance', example: 'Health insurance provider' },
            { name: 'Customer', required: false, description: 'Customer organization name (insurance is unique per customer; will be created if not exists)', example: 'Acme Corporation', format: 'Customer name (optional)' }
          ],
          exampleData: 'Name,Description,Customer\nBlue Cross Blue Shield,Health insurance provider,Acme Corporation\nAetna,Health insurance provider,Acme Corporation\nUnited Healthcare,Health insurance provider,MedTech Solutions\nCigna,Health insurance provider,MedTech Solutions\nMedicare,Government health insurance,'
        };
      case 'locations':
        return {
          title: 'Import Locations',
          description: 'Upload a CSV file with location data. Hover over any column name below for details.',
          requiredColumns: [],
          columnGrid: [
            { name: 'City', required: true, description: 'City name', example: 'New York' },
            { name: 'State', required: true, description: 'State code or name (e.g., NY or New York)', example: 'NY' },
            { name: 'ZipCode', required: false, description: 'Postal zip code', example: '10001', format: 'Numeric or text' },
            { name: 'Address', required: false, description: 'Physical street address (can also use "Physical Address" or "Street")', example: '123 Main St', format: 'Street address' },
            { name: 'Physical Address', required: false, description: 'Physical street address (alternative to "Address")', example: '123 Main St', format: 'Street address' },
            { name: 'Street', required: false, description: 'Physical street address (alternative to "Address")', example: '123 Main St', format: 'Street address' },
            { name: 'Customer', required: false, description: 'Customer organization name (will be created if not exists)', example: 'Acme Corporation', format: 'Customer name (optional)' }
          ],
          exampleData: 'City,State,ZipCode,Address,Customer\nNew York,NY,10001,123 Broadway,Acme Corporation\nLos Angeles,CA,90210,456 Sunset Blvd,MedTech Solutions\nChicago,IL,60601,789 Michigan Ave,Acme Corporation\nBoston,MA,02101,100 Main Street,Regional Medical Center\nSeattle,WA,98101,200 Pine Avenue,HealthPlus\nPortland,OR,97201,300 Oak Street,MedTech Solutions\nDenver,CO,80202,400 Elm Drive,Regional Medical Center'
        };
      case 'conditions':
        return {
          title: 'Import Conditions',
          description: 'Upload a CSV file with medical conditions. Hover over any column name below for details.',
          requiredColumns: [],
          columnGrid: [
            { name: 'Condition', required: true, description: 'Medical condition name', example: 'Diabetes' },
            { name: 'Description', required: false, description: 'Additional details about the condition', example: 'Blood sugar disorder' },
            { name: 'CanCancel', required: false, description: 'Whether appointments for this condition can be cancelled (Yes/No/See Notes)', example: 'Yes', format: 'Yes, No, or See Notes (default: Yes)' },
            { name: 'CanReschedule', required: false, description: 'Whether appointments for this condition can be rescheduled (Yes/No/See Notes)', example: 'Yes', format: 'Yes, No, or See Notes (default: Yes)' },
            { name: 'CanSchedule', required: false, description: 'Whether appointments for this condition can be scheduled (Yes/No/See Notes)', example: 'Yes', format: 'Yes, No, or See Notes (default: Yes)' }
          ],
          exampleData: 'Condition,Description,CanCancel,CanReschedule,CanSchedule\nDiabetes,Blood sugar disorder,Yes,Yes,Yes\nHypertension,High blood pressure,No,Yes,Yes\nAsthma,Respiratory condition,See Notes,See Notes,Yes\nCardiology,Heart and cardiovascular conditions,Yes,Yes,Yes\nNeurology,Nervous system conditions,Yes,Yes,No'
        };
      case 'customers':
        return {
          title: 'Import Customers',
          description: 'Upload a CSV file with customer data. Hover over any column name below for details.',
          requiredColumns: [],
          columnGrid: [
            { name: 'Name', required: true, description: 'Customer organization name', example: 'Acme Corporation' },
            { name: 'Description', required: false, description: 'Additional details about the customer', example: 'Healthcare provider' }
          ],
          exampleData: 'Name,Description\nAcme Corporation,Healthcare provider\nMedTech Solutions,Medical technology company\nHealthPlus,Healthcare services provider\nRegional Medical Center,Hospital network\nCommunity Health Group,Community healthcare organization'
        };
      case 'appointment-details':
        return {
          title: 'Import Appointment Details',
          description: 'Upload a CSV file with appointment details. Hover over any column name below for details.',
          requiredColumns: [],
          columnGrid: [
            { name: 'Name', required: true, description: 'Appointment detail name', example: 'Initial Consultation' },
            { name: 'Customer', required: false, description: 'Customer organization name (will be created if not exists)', example: 'Acme Corporation' },
            { name: 'Can Cancel', required: false, description: 'Whether appointment can be cancelled. Accepts (case-insensitive): "Yes", "No", "See Notes", or legacy boolean values ("true"/"false" which convert to "Yes"/"No"). Defaults to "Yes" if empty or not provided.', example: 'Yes', format: 'Yes | No | See Notes | true | false (case-insensitive, defaults to "Yes")' },
            { name: 'Can Reschedule', required: false, description: 'Whether appointment can be rescheduled. Accepts (case-insensitive): "Yes", "No", "See Notes", or legacy boolean values ("true"/"false" which convert to "Yes"/"No"). Defaults to "Yes" if empty or not provided.', example: 'Yes', format: 'Yes | No | See Notes | true | false (case-insensitive, defaults to "Yes")' },
            { name: 'Can Schedule', required: false, description: 'Whether appointment can be scheduled. Accepts (case-insensitive): "Yes", "No", "See Notes", or legacy boolean values ("true"/"false" which convert to "Yes"/"No"). Defaults to "No" if empty or not provided.', example: 'Yes', format: 'Yes | No | See Notes | true | false (case-insensitive, defaults to "No")' },
            { name: 'Notes', required: false, description: 'Multiple notes separated by pipe character', example: 'Note 1|Note 2|Note 3', format: 'Pipe-delimited (|)' }
          ],
          exampleData: 'Name,Customer,Can Cancel,Can Reschedule,Can Schedule,Notes\nInitial Consultation,Acme Corporation,Yes,Yes,Yes,Bring insurance card|Arrive 15 minutes early\nFollow-up,Beta Healthcare,No,Yes,Yes,No referral needed\nSurgery Consult,Gamma Medical,See Notes,Yes,Yes,Fasting required|Pre-op clearance needed\nEmergency Visit,,Yes,Yes,No,Walk-in only|No appointment needed\nRoutine Checkup,Acme Corporation,Yes,Yes,Yes,Annual wellness visit'
        };
      case 'providers':
        return {
          title: 'Import Providers',
          description: 'Upload a CSV file with comprehensive provider data. Hover over any column name below for details and formatting instructions.',
          requiredColumns: [],
          columnGrid: [
            { name: 'FirstName', required: true, description: 'Provider\'s first name', example: 'John' },
            { name: 'LastName', required: true, description: 'Provider\'s last name', example: 'Smith' },
            { name: 'CustomerName', required: true, description: 'Customer organization name', example: 'Acme Corporation' },
            { name: 'Title', required: false, description: 'Professional title', example: 'MD' },
            { name: 'Specialty', required: false, description: 'Medical specialty (pipe-separated if multiple) - will be converted to conditions', example: 'Cardiology|Interventional Cardiology', format: 'Single specialty or Specialty1|Specialty2|Specialty3' },
            { name: 'Email', required: false, description: 'Contact email address', example: 'john.smith@example.com' },
            { name: 'Phone', required: false, description: 'Primary phone number', example: '555-123-4567' },
            { name: 'Website', required: false, description: 'Website URL', example: 'https://drsmith.com' },
            { name: 'LicenseNumber', required: false, description: 'Medical license number', example: 'MD12345' },
            { name: 'NPINumber', required: false, description: 'National Provider Identifier', example: '1234567890' },
            { name: 'AcceptsNewPatients', required: false, description: 'Accepts new patients?', example: 'true', format: 'true | false | yes | no | 1 | 0' },
            { name: 'RequiresReferral', required: false, description: 'Requires referral?', example: 'false', format: 'true | false | yes | no | 1 | 0' },
            { name: 'SchedulingInstructions', required: false, description: 'How to schedule appointments', example: 'Call ahead for appointments' },
            { name: 'InsuranceNotes', required: false, description: 'Insurance acceptance notes', example: 'Accepts most insurances' },
            { name: 'OfficeSuite', required: false, description: 'Suite or unit number', example: 'Suite 100' },
            { name: 'OfficePhone', required: false, description: 'Main office phone', example: '555-111-2222' },
            { name: 'ClinicalPhone', required: false, description: 'Clinical department phone', example: '555-333-4444' },
            { name: 'FrontDeskPhone', required: false, description: 'Front desk phone', example: '555-555-6666' },
            { name: 'MALVNName', required: false, description: 'MA/LVN staff name', example: 'Mary Johnson' },
            { name: 'MALVNPhone', required: false, description: 'MA/LVN phone number', example: '555-777-8888' },
            { name: 'TriageNurseName', required: false, description: 'Triage nurse name', example: 'Jane Doe' },
            { name: 'TriageNursePhone', required: false, description: 'Triage nurse phone', example: '555-999-0000' },
            { name: 'SurgerySchedulerName', required: false, description: 'Surgery scheduler name', example: 'Bob Wilson' },
            { name: 'SurgerySchedulerPhone', required: false, description: 'Surgery scheduler phone', example: '555-111-3333' },
            { name: 'PracticeAdminName', required: false, description: 'Practice admin name', example: 'Alice Brown' },
            { name: 'PracticeAdminPhone', required: false, description: 'Practice admin phone', example: '555-222-4444' },
            { name: 'MinimumAge', required: false, description: 'Minimum patient age', example: '18', format: 'Integer' },
            { name: 'MaximumAge', required: false, description: 'Maximum patient age', example: '65', format: 'Integer' },
            { name: 'AgeRestrictionNotes', required: false, description: 'Notes about age restrictions', example: 'Pediatric patients only' },
            { name: 'Locations', required: false, description: 'Pipe-separated locations in format "City, State ZipCode" or "City, State ZipCode:PhysicalAddress"', example: 'Boston, MA 02101|Chicago, IL 60601:123 Main St', format: 'City, State ZipCode|City, State ZipCode:PhysicalAddress (locations will be created if not exist)' },
            { name: 'Insurances', required: false, description: 'Pipe-separated insurance company names', example: 'Blue Cross|Aetna|Medicare', format: 'Insurance1|Insurance2|Insurance3 (insurances will be created if not exist)' },
            { name: 'Conditions', required: false, description: 'Pipe-separated condition names (specialty conditions are auto-added)', example: 'Diabetes|Hypertension', format: 'Condition1|Condition2|Condition3 (conditions will be created if not exist)' },
            { name: 'PhoneNumbers', required: false, description: 'Pipe-separated phone entries with format "Name:PhoneNumber:Location"', example: 'Front Desk:555-111-2222:Boston, MA 02101|Nurse Line:555-333-4444', format: 'Name:Number:Location|Name:Number:Location (location is optional)' },
            { name: 'Notes', required: false, description: 'Pipe-separated provider notes', example: 'Available Mon-Fri|Accepts new patients|Telehealth available', format: 'Note 1|Note 2|Note 3' }
          ],
          exampleData: 'FirstName,LastName,CustomerName,Title,Specialty,Email,Phone,Website,LicenseNumber,NPINumber,AcceptsNewPatients,RequiresReferral,SchedulingInstructions,InsuranceNotes,OfficeSuite,OfficePhone,ClinicalPhone,FrontDeskPhone,MALVNName,MALVNPhone,TriageNurseName,TriageNursePhone,SurgerySchedulerName,SurgerySchedulerPhone,PracticeAdminName,PracticeAdminPhone,MinimumAge,MaximumAge,AgeRestrictionNotes,Locations,Insurances,Conditions,PhoneNumbers,Notes\n' +
            'John,Smith,Acme Corporation,MD,Cardiology|Interventional Cardiology,john.smith@example.com,555-123-4567,https://drsmith.com,MD12345,1234567890,true,false,Call ahead for appointments,Accepts most insurances,Suite 100,555-111-2222,555-333-4444,555-555-6666,Mary Johnson,555-777-8888,Jane Doe,555-999-0000,Bob Wilson,555-111-3333,Alice Brown,555-222-4444,18,65,Adult patients only,"Boston, MA 02101:123 Main Street|Chicago, IL 60601:456 Michigan Avenue","Blue Cross|Aetna|Medicare","Diabetes|Hypertension","Front Desk:555-111-2222:Boston, MA 02101|Nurse Line:555-333-4444:Chicago, IL 60601","Available Mon-Fri|Accepts new patients|Telehealth available"\n' +
            'Jane,Doe,MedTech Solutions,DO,Neurology|Headache Medicine,jane.doe@example.com,555-987-6543,https://drjanedoe.com,DO54321,0987654321,true,true,Referral required,Contact insurance for coverage,Floor 2,555-222-3333,555-444-5555,555-666-7777,Tom Smith,555-888-9999,Sarah Lee,555-000-1111,Mike Davis,555-222-5555,Emma White,555-333-6666,0,100,All ages,"New York, NY 10001:789 Broadway","United Healthcare|Cigna","Migraine|Epilepsy","Reception:555-222-3333:New York, NY 10001","Specializes in headache disorders|Evening appointments available"\n' +
            'Michael,Johnson,Regional Medical Center,MD,Internal Medicine|Primary Care,michael.johnson@rmc.com,555-234-5678,https://drmichaeljohnson.com,MD23456,2345678901,true,false,Walk-ins welcome,Most major insurances accepted,Suite 200,555-444-5555,555-666-7777,555-888-9999,Lisa Brown,555-111-2222,Robert Taylor,555-333-4444,David Martinez,555-555-6666,Jennifer Wilson,555-777-8888,21,99,Adult and senior care,"Los Angeles, CA 90210:789 Sunset Blvd","Blue Cross|Medicare|Medicaid","Hypertension|Diabetes|Preventive Care","Main Line:555-444-5555:Los Angeles, CA 90210","Same-day appointments|Telehealth available|Bilingual staff"'
        };
      default:
        return {
          title: 'Import Data',
          description: 'Upload a CSV file',
          requiredColumns: [],
          exampleData: ''
        };
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        // Call the parent handler to close this modal and show preview
        if (onFileSelected) {
          onFileSelected(content);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      setError('Please select a valid CSV file');
    }
  };

  const handleClose = () => {
    setStage('upload');
    setFile(null);
    setImportResult(null);
    setError(null);
    onClose();
  };

  const downloadTemplate = () => {
    const info = getImportTypeInfo();
    const blob = new Blob([info.exampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${importType}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStageContent = () => {
    const info = getImportTypeInfo();

    switch (stage) {
      case 'upload':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                {info.title}
              </h3>
              <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                {info.description}
              </p>
            </div>

            <div className="space-y-4">
              {info.columnGrid ? (
                <div>
                  <h4 className="font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
                    Available Columns <span className="text-sm font-normal" style={{ color: 'var(--color-text-muted)' }}>(hover for details)</span>
                  </h4>
                  <div 
                    className="grid gap-2 p-2 mb-40 rounded-lg" 
                    style={{ 
                      backgroundColor: 'var(--color-bg-secondary)',
                      gridTemplateColumns: info.columnGrid.length <= 3 ? `repeat(${info.columnGrid.length}, minmax(0, 1fr))` : 'repeat(3, minmax(0, 1fr))'
                    }}
                  >
                    {info.columnGrid.map((column: any, index: number) => (
                      <div
                        key={index}
                        className="relative group"
                      >
                        <div
                          className="px-3 py-2 rounded text-sm font-medium cursor-help transition-all"
                          style={{
                            backgroundColor: column.required ? 'var(--color-accent-bg)' : 'var(--color-bg-primary)',
                            borderLeft: column.required ? '3px solid var(--color-accent)' : '3px solid var(--color-border)',
                            color: 'var(--color-text-primary)'
                          }}
                        >
                          {column.name}
                          {column.required && <span className="ml-1 text-xs" style={{ color: 'var(--color-accent)' }}>*</span>}
                        </div>
                        
                        {/* Hover Tooltip */}
                        <div
                          className={`absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 top-full mt-2 p-3 rounded-lg shadow-lg min-w-64 max-w-sm ${
                            (index + 1) % 3 === 0 ? 'right-0' : 'left-0'
                          }`}
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            border: '1px solid var(--color-border)',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                          }}
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <span className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
                                {column.name}
                              </span>
                              {column.required ? (
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}>
                                  Required
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }}>
                                  Optional
                                </span>
                              )}
                            </div>
                            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                              {column.description}
                            </p>
                            {column.format && (
                              <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                                <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>Format:</p>
                                <code className="text-xs px-2 py-1 rounded block" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-accent)' }}>
                                  {column.format}
                                </code>
                              </div>
                            )}
                            <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                              <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>Example:</p>
                              <code className="text-xs px-2 py-1 rounded block break-all" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-success)' }}>
                                {column.example}
                              </code>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : info.requiredColumns.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Required Columns:
                  </h4>
                  <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {info.requiredColumns.map((column, index) => (
                      <li key={index}>• {column}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={downloadTemplate}
                  className="px-4 py-2 border rounded-lg transition-colors flex items-center space-x-2"
                  style={{ 
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Download className="w-4 h-4" />
                  <span>Download Template</span>
                </button>
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500 mb-4">Drag and drop your CSV file here, or click to browse</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Choose File
              </button>
            </div>

            {file && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4" style={{ backgroundColor: 'var(--color-success-bg)', borderColor: 'var(--color-success)' }}>
                <div className="flex items-center">
                  <FileText className="w-5 h-5 mr-2" style={{ color: 'var(--color-success)' }} />
                  <span className="font-medium" style={{ color: 'var(--color-success)' }}>
                    {file.name} selected
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4" style={{ backgroundColor: 'var(--color-error-bg)', borderColor: 'var(--color-error)' }}>
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" style={{ color: 'var(--color-error)' }} />
                  <span style={{ color: 'var(--color-error)' }}>{error}</span>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border rounded-lg transition-colors"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                style={{ 
                  backgroundColor: 'var(--color-accent)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                }}
              >
                <Upload className="w-4 h-4" />
                <span>Choose File</span>
              </button>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center animate-spin" style={{ backgroundColor: 'var(--color-accent-bg)' }}>
              <Upload className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Processing Import
            </h3>
            <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              Please wait while we process your CSV file...
            </p>
          </div>
        );

      case 'completed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-success-bg)' }}>
              <CheckCircle className="w-8 h-8" style={{ color: 'var(--color-success)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Import Completed
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              {importResult?.message || 'Data imported successfully'}
            </p>
            
            {importResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--color-success-bg)', borderColor: 'var(--color-success)' }}>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-medium" style={{ color: 'var(--color-success)' }}>Imported</div>
                    <div style={{ color: 'var(--color-text-secondary)' }}>{importResult.importedCount}</div>
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: 'var(--color-warning)' }}>Skipped</div>
                    <div style={{ color: 'var(--color-text-secondary)' }}>{importResult.skippedCount}</div>
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: 'var(--color-error)' }}>Errors</div>
                    <div style={{ color: 'var(--color-text-secondary)' }}>{importResult.errorCount}</div>
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={handleClose}
              className="px-4 py-2 text-white rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-success)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success)';
              }}
            >
              Close
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-error-bg)' }}>
              <AlertTriangle className="w-8 h-8" style={{ color: 'var(--color-error)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Import Failed
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              {importResult?.message || error || 'An error occurred during import'}
            </p>
            
            {importResult?.errors && importResult.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--color-error-bg)', borderColor: 'var(--color-error)' }}>
                <h4 className="font-medium mb-2" style={{ color: 'var(--color-error)' }}>Errors:</h4>
                <div className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {importResult.errors.slice(0, 5).map((error: any, index: number) => (
                    <div key={index}>
                      Row {error.row}: {error.message}
                    </div>
                  ))}
                  {importResult.errors.length > 5 && (
                    <div>... and {importResult.errors.length - 5} more errors</div>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border rounded-lg transition-colors"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Close
              </button>
              <button
                onClick={() => setStage('upload')}
                className="px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: 'var(--color-accent)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
      style={{ 
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: 0,
        margin: 0
      }}
      onClick={handleClose}
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)',
          marginTop: 'auto',
          marginBottom: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            CSV Import
          </h3>
            <button
              onClick={handleClose}
              className="p-1 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto overflow-x-visible flex-1 custom-scrollbar">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {getStageContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
